// Global filter bar (FM-15).
//
// The data-quality toggle is the requirement: every figure on the page can be
// switched between all cohorts and only cohorts whose wear compliance clears
// RELIABLE_CONFIDENCE_MIN, so a viewer can see how much of a headline number
// rests on thin data. Mirrors the existing `reliableDataOnly` filter on the
// other admin dashboards (PEAK-60) rather than inventing new language.
//
// Native <select> on purpose: the repo has no Radix select dependency and this
// page shouldn't add one for two dropdowns.

import React from 'react';
import { ChevronDown, DatabaseZap } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DataQualityMode, FatigueFilters, FatigueWindow, ShiftType } from '../data/types';
import { RELIABLE_CONFIDENCE_MIN } from '../data/methodology';

const SHIFT_OPTIONS: { value: string; label: string }[] = [
  { value: 'All shifts', label: 'All shifts' },
  ...(['day', 'night', 'rotating', 'mixed', 'office'] as ShiftType[]).map((s) => ({
    value: s,
    label: s.charAt(0).toUpperCase() + s.slice(1),
  })),
];

const QUALITY_OPTIONS: { value: DataQualityMode; label: string; hint: string }[] = [
  { value: 'full', label: 'Everyone', hint: 'Every participant, whatever their wear compliance' },
  {
    value: 'reliable',
    label: 'Reliable data only',
    // The wear threshold behind this lives on the API (it is the same one the
    // other admin dashboards use), so the copy does not restate a number here
    // that the server owns.
    hint: 'Only participants who wore a device for enough of the window to trust',
  },
];

const Select: React.FC<{
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}> = ({ label, value, options, onChange }) => (
  <label className='flex min-w-0 flex-col gap-1'>
    <span className='text-xs font-medium text-muted-foreground'>{label}</span>
    <span className='relative'>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className='h-9 w-full appearance-none rounded-md border border-input bg-card pr-8 pl-3 text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown
        className='pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-muted-foreground'
        aria-hidden
      />
    </span>
  </label>
);

export const GlobalFilters: React.FC<{
  filters: FatigueFilters;
  sites: string[];
  onChange: (filters: FatigueFilters) => void;
  /** Cohorts too small to show their own figures, named for the caveat line. */
  suppressedCohorts: string[];
  minCohortSize?: number;
  /** The window every figure on the page describes. */
  window?: FatigueWindow;
}> = ({ filters, sites, onChange, suppressedCohorts, minCohortSize, window }) => {
  const activeQuality = QUALITY_OPTIONS.find((o) => o.value === filters.dataQuality)!;

  return (
    <div className='flex flex-col gap-3 rounded-lg border border-border bg-card p-4'>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]'>
        <Select
          label='Site'
          value={filters.site}
          options={[{ value: 'All sites', label: 'All sites' }, ...sites.map((s) => ({ value: s, label: s }))]}
          onChange={(site) => onChange({ ...filters, site })}
        />
        <Select
          label='Shift pattern'
          value={filters.shiftType}
          options={SHIFT_OPTIONS}
          onChange={(shiftType) => onChange({ ...filters, shiftType })}
        />

        <div className='flex flex-col gap-1'>
          <span className='text-xs font-medium text-muted-foreground'>Data quality</span>
          <div
            role='radiogroup'
            aria-label='Data quality'
            className='inline-flex h-9 rounded-md border border-input p-0.5'
          >
            {QUALITY_OPTIONS.map((option) => {
              const selected = option.value === filters.dataQuality;
              return (
                <button
                  key={option.value}
                  type='button'
                  role='radio'
                  aria-checked={selected}
                  title={option.hint}
                  onClick={() => onChange({ ...filters, dataQuality: option.value })}
                  className={cn(
                    'rounded-sm px-3 text-sm font-medium whitespace-nowrap transition-colors',
                    selected ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <p className='flex items-start gap-2 text-xs text-muted-foreground'>
        <DatabaseZap className='mt-0.5 size-3.5 shrink-0' aria-hidden />
        <span>
          {activeQuality.hint}
          {window ? `, ${window.start} to ${window.end}` : ''}.
          {suppressedCohorts.length > 0 &&
            ` Fewer than ${minCohortSize ?? 5} people with data in ${suppressedCohorts.join(', ')}, so those teams show no figures of their own — their people still count towards the headline numbers.`}
        </span>
      </p>
    </div>
  );
};
